import { createSlice } from "@reduxjs/toolkit";
import { setIsPolygonPlacementMode } from "./polygonPlacement";
import { dispatch } from "./store";

const initialState = {
  requestBox: {
    isOpen: true,
    isSubmitting: false,
    error: null,
  },
  isPVDesignActive: false,
  requests: [],
  resultsByRequestId: {},
  selectedRequestId: null,
  selectedPanelIds: {},
  cart: [],
  activeDesignIndexByRequestId: {},
  isCartOpen: false,
  selectedRoofs: [],
  isSelectRoofsOpen: false,
};

const pvDesignSlice = createSlice({
  name: "pvDesign",
  initialState,
  reducers: {
    resetPVDesign: (state) => {
      state = initialState;
    },
    setPVDesignRequestBoxOpen: (state, action) => {
      state.requestBox.isOpen = action.payload;
    },
    setIsPVDesignActive: (state, action) => {
      state.isPVDesignActive = action.payload;
    },
    setPVDesignSubmitting: (state, action) => {
      state.requestBox.isSubmitting = action.payload;
    },
    setPVDesignError: (state, action) => {
      state.requestBox.error = action.payload;
    },
    setPVRequests: (state, action) => {
      state.requests = action.payload || [];
    },
    addPVRequest: (state, action) => {
      state.requests = [action.payload, ...state.requests];
    },
    updatePVRequestStatus: (state, action) => {
      const { id, status, recordsCount } = action.payload;
      state.requests = state.requests.map((r) =>
        r.id === id
          ? { ...r, status, recordsCount: recordsCount ?? r.recordsCount }
          : r
      );
    },
    setPVResultsForRequest: (state, action) => {
      const { requestId, designs } = action.payload;
      state.resultsByRequestId[requestId] = designs || [];
    },
    setSelectedRequestId: (state, action) => {
      state.selectedRequestId = action.payload;
      state.selectedPanelIds = {};
    },
    togglePanelSelection: (state, action) => {
      const panelId = action.payload;
      const prev = state.selectedPanelIds || {};
      state.selectedPanelIds = { ...prev, [panelId]: !prev[panelId] };
    },
    removePanelsFromRequest: (state, action) => {
      const { requestId, panelIds } = action.payload;
      const removeIdSet = new Set((panelIds || []).map((id) => String(id)));
      const designs = state.resultsByRequestId[requestId] || [];
      const updated = designs.map((design) => ({
        ...design,
        panels: (design.panels || []).filter(
          (p) => !removeIdSet.has(String(p.id))
        ),
      }));
      state.resultsByRequestId[requestId] = updated;
      state.requests = state.requests.map((r) =>
        r.id === requestId
          ? {
              ...r,
              recordsCount: updated.reduce(
                (sum, d) => sum + (d.panels?.length || 0),
                0
              ),
            }
          : r
      );
      state.selectedPanelIds = {};
    },
    setActiveDesignIndex: (state, action) => {
      const { requestId, index } = action.payload;
      state.activeDesignIndexByRequestId[requestId] = index;
    },
    addToPVCart: (state, action) => {
      const item = action.payload;
      if (!state.cart.find((c) => c.id === item.id)) {
        state.cart = [item, ...state.cart];
      }
      state.isCartOpen = true;
    },
    removeFromPVCart: (state, action) => {
      const id = action.payload;
      state.cart = state.cart.filter((c) => c.id !== id);
    },
    updateCartItem: (state, action) => {
      const { id, changes } = action.payload;
      state.cart = state.cart.map((c) =>
        c.id === id ? { ...c, ...changes } : c
      );
    },
    setCartOpen: (state, action) => {
      state.isCartOpen = action.payload;
    },
    clearSelectedPanels: (state) => {
      state.selectedPanelIds = {};
    },
    setSelectedRoofs: (state, action) => {
      state.selectedRoofs = action.payload;
    },
    setSelectedRoof: (state, action) => {
      state.selectedRoofs = action.payload;
    },
    setIsSelectRoofOpen: (state, action) => {
      state.isSelectRoofsOpen = action.payload;
    },
  },
});

export const {
  setPVDesignRequestBoxOpen,
  setIsPVDesignActive,
  setPVDesignSubmitting,
  setPVDesignError,
  setPVRequests,
  addPVRequest,
  updatePVRequestStatus,
  setPVResultsForRequest,
  setSelectedRequestId,
  togglePanelSelection,
  removePanelsFromRequest,
  setActiveDesignIndex,
  addToPVCart,
  removeFromPVCart,
  updateCartItem,
  setCartOpen,
  clearSelectedPanels,
  setSelectedRoofs,
  setSelectedRoof,
  setIsSelectRoofOpen,
  resetPVDesign,
} = pvDesignSlice.actions;

export default pvDesignSlice.reducer;

export const closePVDesign = () => {
  return () => {
    dispatch(resetPVDesign());
    dispatch(setPVDesignRequestBoxOpen(false));
    dispatch(setIsPolygonPlacementMode(false));
  };
};

export const openPVDesign = () => {
  return () => {
    dispatch(resetPVDesign());
    dispatch(setIsPVDesignActive(true));
    dispatch(setPVDesignRequestBoxOpen(true));
    dispatch(setIsPolygonPlacementMode(false));
  };
};
